// DAS-1571: Compute portfolio summary with per-ticker stats
// DAS-1714: Added net result calculation per ticker
export const computePortfolioSummary = (equityCurve, eventsList) => {
  if (!equityCurve || equityCurve.length === 0) return null;
  
  // Get start and end holdings
  const startEntry = equityCurve[0];
  const endEntry = equityCurve[equityCurve.length - 1];
  
  const parseHoldings = (entry) => {
    try {
      const holdingsJson = entry?.holdings_json;
      if (!holdingsJson) return [];
      if (typeof holdingsJson === 'string') {
        return JSON.parse(holdingsJson);
      }
      if (Array.isArray(holdingsJson)) {
        return holdingsJson;
      }
      return [];
    } catch {
      return [];
    }
  };
  
  const startHoldings = parseHoldings(startEntry);
  const endHoldings = parseHoldings(endEntry);
  
  // Build ticker stats map
  const tickerStats = {};
  
  // DAS-1714: Helper to initialize ticker stats
  const initTickerStats = (ticker, isInitial = false) => ({
    ticker,
    start_qty: 0,
    end_qty: 0,
    dividends_eur: 0,
    buy_count: 0,
    sell_count: 0,
    was_initial: isInitial,
    was_added: !isInitial,
    // DAS-1714: Track cash flows for net result
    buy_value_eur: 0,      // Total cost of buys
    sell_value_eur: 0,     // Total proceeds from sells
    fees_eur: 0,           // Total transaction fees
    net_result_eur: null,  // Calculated at end
  });
  
  // Initialize with start holdings
  startHoldings.forEach(h => {
    const ticker = h.ticker;
    if (!ticker) return;
    tickerStats[ticker] = initTickerStats(ticker, true);
    tickerStats[ticker].start_qty = h.quantity || 0;
  });
  
  // Add end holdings
  endHoldings.forEach(h => {
    const ticker = h.ticker;
    if (!ticker) return;
    if (!tickerStats[ticker]) {
      tickerStats[ticker] = initTickerStats(ticker, false);
    }
    tickerStats[ticker].end_qty = h.quantity || 0;
  });
  
  // Aggregate events per ticker
  (eventsList || []).forEach(ev => {
    const ticker = ev.ticker;
    if (!ticker) return;
    
    if (!tickerStats[ticker]) {
      tickerStats[ticker] = initTickerStats(ticker, false);
    }
    
    // DAS-1714: Helper to safely parse numeric values (handles Decimal from MySQL, strings, null)
    const safeNum = (val) => {
      if (val == null) return 0;
      const n = typeof val === 'string' ? parseFloat(val) : Number(val);
      return isNaN(n) ? 0 : n;
    };
    
    const eventType = ev.event_type;
    if (eventType === 'dividend') {
      const qty = safeNum(ev.qty);
      const price = safeNum(ev.price);
      tickerStats[ticker].dividends_eur += qty * price;
    } else if (eventType === 'trade') {
      const qty = safeNum(ev.qty);
      const price = safeNum(ev.price);
      const fees = safeNum(ev.fees);
      
      if (qty > 0) {
        // Buy: we spend money
        tickerStats[ticker].buy_count += 1;
        tickerStats[ticker].buy_value_eur += qty * price;
        tickerStats[ticker].fees_eur += fees;
        if (tickerStats[ticker].start_qty === 0) {
          tickerStats[ticker].was_added = true;
        }
      } else if (qty < 0) {
        // Sell: we receive money
        tickerStats[ticker].sell_count += 1;
        tickerStats[ticker].sell_value_eur += Math.abs(qty) * price;
        tickerStats[ticker].fees_eur += fees;
      }
    }
  });
  
  // DAS-1714: Calculate net result for each ticker
  // Net Result = Sell Proceeds - Buy Costs + Dividends - Fees
  // Note: For open positions, this shows realized cash flow only (excludes unrealized gains)
  Object.values(tickerStats).forEach(stats => {
    const result = 
      stats.sell_value_eur 
      - stats.buy_value_eur 
      + stats.dividends_eur 
      - stats.fees_eur;
    // Only set if we have valid numbers (not NaN)
    stats.net_result_eur = isNaN(result) ? null : result;
  });
  
  return {
    start_date: startEntry?.pos_date,
    end_date: endEntry?.pos_date,
    tickers: Object.values(tickerStats).sort((a, b) => a.ticker.localeCompare(b.ticker)),
  };
};

export const createEmptyClauseForm = () => ({
  id: null,
  clause_field: '',
  clause_field_sourcetable: '',
  clause_operator: '<',
  threshold_value: null,
  threshold_field: '',
  threshold_sourcetable: '',
  clause_txt: '',
  clause_scope: 'any',
  trade_direction: 'buy',
});

export const createEmptyClauseCombinationForm = () => ({
  id: null,
  name: '',
  description: '',
  clause_ids: [],
});

export const mapClauseItemToForm = (item) => {
  const base = createEmptyClauseForm();
  if (!item) return base;
  return {
    ...base,
    id: item.id ?? base.id,
    clause_field: item.clause_field || base.clause_field,
    clause_field_sourcetable: item.clause_field_sourcetable || base.clause_field_sourcetable,
    clause_operator: item.clause_operator || base.clause_operator,
    threshold_value:
      item.threshold_value === null || item.threshold_value === undefined
        ? base.threshold_value
        : item.threshold_value,
    threshold_field: item.threshold_field || base.threshold_field,
    threshold_sourcetable: item.threshold_sourcetable || base.threshold_sourcetable,
    clause_txt: item.clause_txt || base.clause_txt,
    clause_scope: (item.clause_scope || base.clause_scope).toLowerCase(),
    trade_direction: (item.trade_direction || base.trade_direction).toLowerCase(),
  };
};

export const mapClauseSetToForm = (item) => {
  const base = createEmptyClauseCombinationForm();
  if (!item) return base;
  return {
    ...base,
    id: item.id ?? base.id,
    name: item.name || base.name,
    description: item.description || base.description,
    clause_ids: Array.isArray(item.clause_ids) ? item.clause_ids : base.clause_ids,
  };
};

export const deepEqual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export const clonePlain = (obj) => JSON.parse(JSON.stringify(obj ?? {}));

export const CLAUSE_COOLDOWN_MODE_OPTIONS = [
  { label: 'Days between trades', value: 'days' },
  { label: 'Max trades per run', value: 'count' },
];

